import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import {
  Key, Plus, AlertTriangle, FileWarning, ArrowUpRight, ArrowDownLeft,
  Download, X, CheckCircle2, ChevronRight, Search, Loader2,
} from "lucide-react";
import MandateWizard from "../components/MandateWizard";
import MandateDetailDrawer from "../components/MandateDetailDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  active:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  expired:   "bg-rose-50 text-rose-800 border-rose-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
};

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

export default function MandateHub() {
  const [stats, setStats] = useState(null);
  const [outgoing, setOutgoing] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardDirection, setWizardDirection] = useState(null); // 'outgoing' | 'incoming'
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, i] = await Promise.all([
        api.get("/mandates/stats"),
        api.get("/mandates?direction=outgoing"),
        api.get("/mandates?direction=incoming"),
      ]);
      setStats(s.data);
      setOutgoing(o.data || []);
      setIncoming(i.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Deep-link: open a specific mandate drawer when react-router state
  // carries openMandateId. Used by Dashboard urgent feed + notification bell.
  const location = useLocation();
  useEffect(() => {
    const target = location.state?.openMandateId;
    if (!target) return;
    const found = [...outgoing, ...incoming].find((m) => m.id === target);
    if (found) setDetailId(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoing, incoming]);

  const filt = (arr) => {
    if (!q) return arr;
    const s = q.toLowerCase();
    return arr.filter((m) =>
      m.partner_agent_name?.toLowerCase().includes(s) ||
      m.partner_agency_name?.toLowerCase().includes(s) ||
      m.client?.full_name?.toLowerCase().includes(s) ||
      m.scope_description?.toLowerCase().includes(s),
    );
  };

  return (
    <Layout title="Mandates" subtitle="FIFA-grade mandate register · outgoing and incoming">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="mandate-stats">
        <StatCard label="Active mandates" value={stats?.active ?? "—"} tone="emerald" />
        <StatCard label="Expiring · 30 days" value={stats?.expiring_soon ?? "—"} tone="amber" warn={stats?.expiring_soon > 0} />
        <StatCard label="Missing document" value={stats?.no_document ?? "—"} tone="rose" warn={stats?.no_document > 0} />
        <StatCard label="Expired" value={stats?.expired ?? "—"} tone="slate" />
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by player, partner agent, agency, scope…"
            className="pm-input pl-9"
            data-testid="mandate-search-input"
          />
        </div>
      </div>

      {/* Two panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MandatePanel
          title="Outgoing mandates"
          subtitle="You gave permission to other agents to place your clients."
          Icon={ArrowUpRight}
          tone="emerald"
          mandates={filt(outgoing)}
          loading={loading}
          onAdd={() => { setWizardDirection("outgoing"); setWizardOpen(true); }}
          onOpen={(id) => setDetailId(id)}
          testid="panel-outgoing"
        />
        <MandatePanel
          title="Incoming mandates"
          subtitle="Other agents gave you permission to place their players."
          Icon={ArrowDownLeft}
          tone="amber"
          mandates={filt(incoming)}
          loading={loading}
          onAdd={() => { setWizardDirection("incoming"); setWizardOpen(true); }}
          onOpen={(id) => setDetailId(id)}
          testid="panel-incoming"
        />
      </div>

      {wizardOpen && (
        <MandateWizard
          direction={wizardDirection}
          onClose={() => setWizardOpen(false)}
          onCreated={() => { setWizardOpen(false); load(); }}
        />
      )}
      {detailId && (
        <MandateDetailDrawer
          mandateId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => load()}
        />
      )}
    </Layout>
  );
}

function StatCard({ label, value, tone, warn }) {
  const TONES = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`pm-card p-4 border ${TONES[tone] || ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70">{label}</div>
        {warn && <AlertTriangle size={11} className="opacity-90" />}
      </div>
      <div className="font-display text-3xl font-black tracking-tight mt-1">{value}</div>
    </div>
  );
}

function MandatePanel({ title, subtitle, Icon, tone, mandates, loading, onAdd, onOpen, testid }) {
  const ICON_TONE = tone === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700";
  return (
    <div className="pm-card overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${ICON_TONE}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-bold tracking-tight">{title}</div>
          <div className="text-[12px] text-slate-500">{subtitle}</div>
        </div>
        <button
          onClick={onAdd}
          data-testid={`${testid}-add-btn`}
          className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5 shrink-0"
        >
          <Plus size={11} /> New
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {loading ? (
          <div className="p-6 text-center text-slate-500 flex items-center gap-2 justify-center">
            <Loader2 size={12} className="animate-spin" /> Loading mandates…
          </div>
        ) : mandates.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">
            No {title.toLowerCase()} yet.
          </div>
        ) : (
          mandates.map((m) => <MandateRow key={m.id} m={m} onOpen={() => onOpen(m.id)} />)
        )}
      </div>
    </div>
  );
}

function MandateRow({ m, onOpen }) {
  const d = daysUntil(m.expiry_date);
  const warnExpiry = m.status === "active" && d !== null && d <= 30 && d >= 0;
  const expired = m.status === "expired" || (m.status === "active" && d !== null && d < 0);
  return (
    <button
      onClick={onOpen}
      className={`w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3 ${expired ? "opacity-70" : ""}`}
      data-testid={`mandate-row-${m.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm text-slate-900 truncate">{m.client?.full_name || "—"}</span>
          <span className="text-[12px] text-slate-500 truncate">· {m.partner_agent_name}</span>
          {m.partner_agency_name && <span className="text-[11px] text-slate-400 truncate">({m.partner_agency_name})</span>}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className={`pm-pill ${STATUS_TONE[m.status] || ""} capitalize`}>{m.status}</span>
          <span className="text-[11px] text-slate-500">
            Scope: <b className="text-slate-700">{(m.scope_clubs?.length ? m.scope_clubs.join(", ") : null) || (m.scope_leagues?.length ? m.scope_leagues.join(", ") : null) || m.scope_description || m.scope_type}</b>
          </span>
          {m.expiry_date && (
            <span className={`text-[11px] ${warnExpiry ? "text-amber-700 font-bold" : "text-slate-500"}`}>
              {expired
                ? `Expired ${fmtDate(m.expiry_date)}`
                : `Expires ${fmtDate(m.expiry_date)}${d !== null ? ` · ${d}d` : ""}`}
            </span>
          )}
          {!m.document_filename && m.status === "active" && (
            <span className="text-[11px] text-rose-700 font-bold flex items-center gap-0.5" data-testid={`no-document-warning-${m.id}`}>
              <FileWarning size={10} /> No document
            </span>
          )}
        </div>
      </div>
      <ChevronRight size={14} className="text-slate-300 shrink-0" />
    </button>
  );
}

export { STATUS_TONE };
